// src/components/reports/SummaryStrip.tsx
import React from 'react';
import {
  Paper, Box, Stack, Typography, Skeleton, LinearProgress, Tooltip
} from '@mui/material';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

type Delta = number | undefined;
type KPI = { value: number; mom?: Delta; yoy?: Delta };
type KPIs = {
  income: KPI; expense: KPI; net: KPI; savings: KPI; savingsRate: KPI;
} | null;

const pct = (d?: number) => (d === undefined ? '–' : `${(d * 100).toFixed(0)}%`);
const signColor = (d?: number) => (d === undefined ? 'text.secondary' : d >= 0 ? 'success.main' : 'error.main');

const Metric: React.FC<{ title: string; value?: string; mom?: number; yoy?: number }> = ({
  title, value, mom, yoy
}) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Typography variant="overline" sx={{ opacity: 0.7 }}>{title}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{value ?? '–'}</Typography>
    <Box sx={{ display: 'flex', gap: 2, fontSize: 12 }}>
      <Box sx={{ color: signColor(mom) }}>MoM: {pct(mom)}</Box>
      <Box sx={{ color: signColor(yoy) }}>YoY: {pct(yoy)}</Box>
    </Box>
  </Paper>
);

const SummaryStrip: React.FC<{
  loading: boolean;
  month: string;
  kpis: KPIs;
  budget?: {
    month: string;
    budgeted: number;
    variance: number;
    hasAllBudget?: boolean;
    overBudgetCategoryCount?: number;
  } | undefined;
  savings?: { inRange: number; cumulative: number };
}> = ({ loading, kpis, budget, savings }) => {
  const formatting = useFormatting();
  // Card wrapper style: responsive widths using flexbox
  const cardSx = {
    flexBasis: { xs: '100%', sm: '50%', md: '20%' },
    flexGrow: 1,
    minWidth: 220,
  } as const;

  if (loading) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Box key={i} sx={cardSx}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Skeleton width="60%" />
              <Skeleton variant="text" height={36} />
              <Skeleton width="80%" />
            </Paper>
          </Box>
        ))}
      </Stack>
    );
  }

  if (!kpis) return null;

  const fmt = (v: number, style: 'currency' | 'percent' = 'currency') =>
    style === 'currency'
      ? formatMoney(v, formatting, { maximumFractionDigits: 0 })
      : `${Math.round(v * 100)}%`;

  return (
    <Stack spacing={2}>
      {/* KPI cards */}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <Box sx={cardSx}>
          <Metric title="Income" value={fmt(kpis.income.value)} mom={kpis.income.mom} yoy={kpis.income.yoy} />
        </Box>
        <Box sx={cardSx}>
          <Metric title="Expenses" value={fmt(kpis.expense.value)} mom={kpis.expense.mom} yoy={kpis.expense.yoy} />
        </Box>
        <Box sx={cardSx}>
          <Metric title="Net" value={fmt(kpis.net.value)} mom={kpis.net.mom} yoy={kpis.net.yoy} />
        </Box>
        <Box sx={cardSx}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="overline" sx={{ opacity: 0.7 }}>Transfers</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {fmt(kpis.savings.value)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, fontSize: 12, mb: savings ? 1 : 0 }}>
              <Box sx={{ color: signColor(kpis.savings.mom) }}>MoM: {pct(kpis.savings.mom)}</Box>
              <Box sx={{ color: signColor(kpis.savings.yoy) }}>YoY: {pct(kpis.savings.yoy)}</Box>
            </Box>
            {savings ? (
              <Tooltip title="Cumulative is all-time savings up to today.">
                <Box sx={{ fontSize: 12, opacity: 0.75 }}>
                  Savings this period: {fmt(savings.inRange)} · Cumulative savings: {fmt(savings.cumulative)}
                </Box>
              </Tooltip>
            ) : null}
          </Paper>
        </Box>
        <Box sx={cardSx}>
          <Metric
            title="Savings Rate"
            value={fmt(kpis.savingsRate.value, 'percent')}
            mom={kpis.savingsRate.mom}
          />
        </Box>
      </Stack>

      {/* Budget block (full width) */}
      {budget && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="overline" sx={{ opacity: 0.7 }}>Budget Usage</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Budgeted: {fmt(budget.budgeted)}</Typography>
            <Typography
              variant="body2"
              sx={{ color: budget.variance >= 0 ? 'success.main' : 'error.main' }}
            >
              {budget.variance >= 0
                ? `Under by ${fmt(budget.variance)}`
                : `Over by ${fmt(-budget.variance)}`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (kpis.expense.value / Math.max(1, budget.budgeted)) * 100)}
            sx={{ height: 8, borderRadius: 1 }}
          />
          {(budget.overBudgetCategoryCount || 0) > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              {`Warning: ${budget.overBudgetCategoryCount} category budget(s) over`}
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );
};

export default SummaryStrip;
